// Before Arlo comes home, Today becomes a countdown and preparation hub.

import { BookOpen, CalendarHeart, House, ChevronRight, HeartPulse, MessageSquareQuote, PawPrint, ShoppingBasket, Target } from 'lucide-react';
import { useMemo } from 'react';
import { HOMECOMING } from '../../content/homecoming.ts';
import { RULES } from '../../content/rules.ts';
import { SHOPPING } from '../../content/shopping.ts';
import { useCollection, useLiveStatus, useProfile } from '../../data/hooks.ts';
import { openCare } from '../../domain/care.ts';
import { milestones } from '../../domain/milestones.ts';
import { atMums, prepFocus } from '../../domain/homecoming.ts';
import { daysBetween, formatDate, relativeDay, today } from '../../domain/time.ts';
import { navigate } from '../../app/router.ts';
import { Card, CardHeader, ProgressBar } from '../../ui/primitives.tsx';
import { ReadinessCard } from '../settings/Readiness.tsx';
import { AppPromo } from './AppPromo.tsx';

export function Countdown() {
  const profile = useProfile();
  const s = useLiveStatus(60_000);
  const shopping = useCollection('shopping');
  const rules = useCollection('rules');
  const care = useCollection('care');
  const days = profile.homeDate ? daysBetween(today(s.now), profile.homeDate) : undefined;

  const prep = useMemo(() => {
    const have = new Set(shopping.filter((i) => i.have || i.skip).map((i) => i.id));
    const essentials = SHOPPING.flatMap((c) => c.items).filter((i) => i.beforeHome);
    const agreed = rules.filter((r) => r.answer?.trim()).length;
    const beforeHome = openCare(care).filter((c) => c.due && profile.homeDate && c.due <= profile.homeDate);
    return { essentials, gotEssentials: essentials.filter((i) => have.has(i.id)).length, agreed, beforeHome };
  }, [shopping, rules, care, profile.homeDate]);

  const ticks = useCollection('homecoming');
  const plan = useMemo(() => {
    const done = new Set(ticks.filter((t) => t.done).map((t) => t.id));
    const items = HOMECOMING.flatMap((x) => x.items);
    return { total: items.length, done: items.filter((i) => done.has(i.id)).length };
  }, [ticks]);

  // Jobs from his care plan and his milestones, as one list by date.
  const coming = useMemo(() => {
    const from = today(s.now);
    const jobs = prep.beforeHome.map((c) => ({ id: c.id, title: c.title, date: c.due!, care: true }));
    const marks = profile.dob ? milestones(profile.dob, profile.homeDate).filter((m) => m.date >= from).map((m) => ({ id: m.id, title: m.title, date: m.date, care: false })) : [];
    return [...jobs, ...marks]
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(0, 5)
      .map((x) => ({ ...x, when: x.care ? relativeDay(x.date, s.now) : formatDate(x.date) }));
  }, [prep.beforeHome, profile.dob, profile.homeDate, s.now]);

  return (
    <div className="countdown">
      <Card className="countdown-hero">
        <img className="countdown-photo" src={profile.photo ?? './arlo.jpg'} alt={`${profile.name} as a tiny puppy`} />
        <div className="countdown-text">
          <span className="eyebrow">Getting ready for {profile.name}</span>
          {days !== undefined ? (
            <>
              <h1 className="countdown-days">
                {days === 0 ? 'Home today!' : days === 1 ? 'Home tomorrow' : <>{days} days</>}
              </h1>
              <p className="muted">
                {days > 1 ? `until ${profile.name} comes home · ${formatDate(profile.homeDate!, 'long')}` : 'Deep breath. You’ve got this.'}
              </p>
            </>
          ) : (
            <>
              <h1 className="countdown-days">Coming soon</h1>
              <p className="muted">Add his collection date in Settings to start the countdown.</p>
            </>
          )}
          {days !== undefined && days > 0 && profile.dob && profile.homeDate && <Journey dob={profile.dob} homeDate={profile.homeDate} now={s.now} />}
          {s.age && (
            <p className="muted">
              He’s {s.age.label} old today. <span className="at-mums">{atMums(s.age.weeks)}</span>
            </p>
          )}
        </div>
      </Card>
      <AppPromo />

      {days !== undefined && days > 0 && (
        <button type="button" className="prep-focus" onClick={() => navigate('/homecoming')}>
          <Target aria-hidden />
          <span>
            <span className="eyebrow">{days === 1 ? 'Tonight' : 'Focus this week'}</span>
            <strong>{prepFocus(days).title}</strong>
            <span className="muted">{prepFocus(days).detail}</span>
          </span>
          <ChevronRight aria-hidden />
        </button>
      )}

      <div className="countdown-grid">
        <Card className="countdown-progress">
          <CardHeader title="Getting there" icon={Target} />
          <ul className="progress-rows">
            {[
              { label: 'Homecoming plan', done: plan.done, total: plan.total, to: '/homecoming', icon: House, tone: 'good' as const },
              { label: 'Day-one shopping', done: prep.gotEssentials, total: prep.essentials.length, to: '/shopping', icon: ShoppingBasket, tone: 'accent' as const },
              { label: 'House rules agreed', done: prep.agreed, total: RULES.length, to: '/training/rules', icon: MessageSquareQuote, tone: 'info' as const },
            ].map((r) => (
              <li key={r.label}>
                <button type="button" onClick={() => navigate(r.to)}>
                  <r.icon aria-hidden className="progress-row-icon" />
                  <span className="progress-row-text">
                    <span className="progress-row-head">
                      <strong>{r.label}</strong>
                      <span className="muted small">
                        {r.done} / {r.total}
                      </span>
                    </span>
                    <ProgressBar value={r.total ? r.done / r.total : 0} tone={r.tone} />
                  </span>
                  <ChevronRight aria-hidden className="progress-row-go" />
                </button>
              </li>
            ))}
          </ul>
        </Card>
        <ReadinessCard className="countdown-ready" limit={3} />
        {coming.length > 0 && (
          <Card onClick={() => navigate('/health')}>
            <CardHeader title="Coming up" icon={CalendarHeart} action={<ChevronRight aria-hidden />} />
            <ul className="mini-list">
              {coming.map((c) => (
                <li key={c.id}>
                  {c.care ? <HeartPulse aria-hidden /> : <PawPrint aria-hidden />} {c.title}
                  <span className="muted"> · {c.when}</span>
                </li>
              ))}
            </ul>
          </Card>
        )}
        <Card onClick={() => navigate('/guide/before-he-arrives')}>
          <CardHeader title="Read first" icon={BookOpen} action={<ChevronRight aria-hidden />} />
          <ul className="mini-list">
            <li>Before he arrives — puppy-proofing & breeder questions</li>
            <li>The first day and night</li>
            <li>Toilet training in a flat</li>
          </ul>
        </Card>
      </div>
    </div>
  );
}

/** Born → home, with a paw where he is today: how far along the wait is. */
function Journey({ dob, homeDate, now }: { dob: string; homeDate: string; now: number }) {
  const total = daysBetween(dob, homeDate);
  const done = daysBetween(dob, today(now));
  if (total <= 0) return null;
  const pct = Math.max(0, Math.min(100, (done / total) * 100));
  return (
    <div className="journey" role="img" aria-label={`${Math.round(pct)}% of the way from his birth to coming home`}>
      <div className="journey-bar">
        <i style={{ width: `${pct}%` }} />
        <span className="journey-dot" style={{ left: `${pct}%` }} title="Today">
          <PawPrint aria-hidden />
        </span>
      </div>
      <div className="journey-labels">
        <span>Born {formatDate(dob)}</span>
        <span>Home {formatDate(homeDate)}</span>
      </div>
    </div>
  );
}
